import { useEffect, useState } from "react";
import { PageHeader, SectionDivider } from "@/components/primitives";
import { getStats } from "@/lib/api";
import { Sprout, Coins, Star, LayoutGrid } from "lucide-react";

const STAGE_COLOR = {
  purchased: "#7E8C5B",
  planted: "#A4543A",
  germinated: "#5F6B43",
  flowering: "#C26B4A",
  dormant: "#8A8474",
  finished: "#5A5448",
};

export default function Stats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    getStats().then(setStats).catch(() => {});
  }, []);

  if (!stats) return (
    <div className="text-center mt-20 font-display italic text-ink-soft" data-testid="stats-loading">
      Tallying the season...
    </div>
  );

  const stageTotal = Object.values(stats.by_stage || {}).reduce((a, b) => a + b, 0) || 1;
  const stageEntries = Object.entries(stats.by_stage || {});

  return (
    <div data-testid="stats-page">
      <PageHeader kicker="The Ledger" title="Season Stats" subtitle="A reckoning of the patch" />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2" data-testid="stats-tiles">
        <Tile icon={Sprout} label="Plants" value={stats.total_plants} testid="stat-plants" />
        <Tile icon={Star} label="Favourites" value={stats.favorites} accent testid="stat-favs" />
        <Tile icon={Coins} label="Invested" value={`NZ$ ${stats.total_cost_nzd.toFixed(2)}`} testid="stat-cost" />
        <Tile icon={LayoutGrid} label="Beds" value={stats.bed_count} testid="stat-beds" />
      </div>

      <div className="mt-10">
        <SectionDivider label="By Stage" />
      </div>

      {stageEntries.length === 0 ? (
        <p className="text-center mt-6 font-display italic text-ink-soft">No specimens recorded yet.</p>
      ) : (
        <div className="mt-6 space-y-3" data-testid="stage-rows">
          {stageEntries.map(([stage, count]) => {
            const pct = (count / stageTotal) * 100;
            return (
              <div key={stage} data-testid={`stage-row-${stage}`}>
                <div className="flex justify-between text-sm">
                  <span className="capitalize font-display text-lg text-ink">{stage}</span>
                  <span className="text-ink-soft tabular-nums">{count}</span>
                </div>
                <div className="h-2 bg-cream-deep rounded-full overflow-hidden mt-1">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, background: STAGE_COLOR[stage] || "#7E8C5B" }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-12">
        <SectionDivider label="By Category" />
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        {Object.entries(stats.by_category || {}).map(([c, n]) => (
          <span key={c} className="chip" data-testid={`cat-${c}`}>
            <span className="capitalize">{c}</span>
            <span className="text-ink-soft">· {n}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="h-card p-4 shadow-card" data-testid={testid}>
      <Icon size={18} className={accent ? "text-terracotta" : "text-sage-dark"} />
      <div className="mt-2 font-display text-3xl text-sage-dark tabular-nums">{value}</div>
      <div className="kicker mt-1">{label}</div>
    </div>
  );
}
